<div class="billing-portal-container container card">
	<p-toast></p-toast>
	<div id="mention_table">
		<div class="tyalls-table-name mb-3 flex">
			<h6 class="tyalls-table-heading">Billing Setup<span class="font-light"></span></h6>
		</div>
	</div>

	<p *ngIf="shouldShowForm" class="text-gray-700 mb-4">
		Please complete your billing details to activate your Tyalls account.
		Your information will be reviewed by our team before your first invoice is generated.
	</p>

	<!-- SUCCESS / ALREADY SETUP MESSAGE -->
	<div
		*ngIf="isBillingSetupComplete"
		class="text-center py-6"
	>
		<h5 class="text-xl font-semibold mb-2">
			{{ setupSuccess ? 'Billing Setup Complete' : 'Billing Already Set Up' }}
		</h5>

		<p class="text-gray-700 mb-3">
			{{ setupSuccess
			? 'Your billing details have been successfully configured. You will receive your first invoice soon.'
			: 'This property already has a billing account linked.' }}
		</p>

		<p>
			To make changes or update your billing details, please use the Billing Portal.
		</p>
	</div>

	<!-- Billing Form -->
	<form  [formGroup]="billingForm" (ngSubmit)="onSubmit()" class="p-fluid" *ngIf="!existingBillingSummary?.billingId && !setupSuccess">

		<!-- BUSINESS INFO -->
		<p-panel header="Business Information">
			<div class="grid formgrid">
				<div class="field col-12 md:col-6">
					<label for="legalName">Legal Name *</label>
					<input id="legalName" pInputText formControlName="legalName" />
					<small *ngIf="isInvalid('legalName')" class="p-error">Required</small>
				</div>

				<div class="field col-12 md:col-6">
					<label for="tradingName">Trading Name *</label>
					<input id="tradingName" pInputText formControlName="tradingName" />
					<small *ngIf="isInvalid('tradingName')" class="p-error">Required</small>
				</div>
			</div>
		</p-panel>

		<br />

		<!-- PRIMARY CONTACT -->
		<p-panel header="Primary Contact">
			<div class="grid formgrid">
				<div class="field col-12 md:col-4">
					<label for="primaryContactFirstName">First Name *</label>
					<input id="primaryContactFirstName" pInputText formControlName="primaryContactFirstName" />
					<small *ngIf="isInvalid('primaryContactFirstName')" class="p-error">Required</small>
				</div>

				<div class="field col-12 md:col-4">
					<label for="primaryContactLastName">Last Name *</label>
					<input id="primaryContactLastName" pInputText formControlName="primaryContactLastName" />
					<small *ngIf="isInvalid('primaryContactLastName')" class="p-error">Required</small>
				</div>

				<div class="field col-12 md:col-4">
					<label for="primaryContactEmail">Email *</label>
					<input id="primaryContactEmail" pInputText formControlName="primaryContactEmail" type="email" />
					<small *ngIf="isInvalid('primaryContactEmail')" class="p-error">Valid email required</small>
				</div>
			</div>
		</p-panel>

		<br />

		<!-- SECONDARY CONTACT -->
		<div class="field-checkbox mt-2">
			<p-checkbox formControlName="addSecondaryContact" binary="true" inputId="addSecondaryContact"></p-checkbox>
			<label for="addSecondaryContact">Add an additional email to send a copy of invoices</label>
		</div>

		<p-panel *ngIf="billingForm.get('addSecondaryContact')?.value" header="Secondary Contact">
			<div class="grid formgrid">
				<div class="field col-12 md:col-4">
					<label for="secondaryContactFirstName">First Name</label>
					<input id="secondaryContactFirstName" pInputText formControlName="secondaryContactFirstName" />
				</div>
				<div class="field col-12 md:col-4">
					<label for="secondaryContactLastName">Last Name</label>
					<input id="secondaryContactLastName" pInputText formControlName="secondaryContactLastName" />
				</div>
				<div class="field col-12 md:col-4">
					<label for="secondaryContactEmail">Email</label>
					<input id="secondaryContactEmail" pInputText formControlName="secondaryContactEmail" type="email" />
				</div>
			</div>
		</p-panel>

		<br *ngIf="billingForm.get('addSecondaryContact')?.value" />

		<!-- BILLING ADDRESS -->
		<p-panel header="Billing Address">
			<div class="field">
				<label for="billingAddressLine1">Address Line 1 *</label>
				<input id="billingAddressLine1" pInputText formControlName="billingAddressLine1" />
				<small *ngIf="isInvalid('billingAddressLine1')" class="p-error">Required</small>
			</div>

			<div class="grid formgrid">
				<div class="field col-12 md:col-4">
					<label for="billingAddressCity">City *</label>
					<input id="billingAddressCity" pInputText formControlName="billingAddressCity" />
					<small *ngIf="isInvalid('billingAddressCity')" class="p-error">Required</small>
				</div>
				<div class="field col-12 md:col-4">
					<label for="billingAddressState">State</label>
					<input id="billingAddressState" pInputText formControlName="billingAddressState" />
				</div>
				<div class="field col-12 md:col-4">
					<label for="billingAddressZipCode">Postcode *</label>
					<input id="billingAddressZipCode" pInputText formControlName="billingAddressZipCode" />
					<small *ngIf="isInvalid('billingAddressZipCode')" class="p-error">Required</small>
				</div>
			</div>

			<div class="grid formgrid">
				<div class="field col-12 md:col-6">
					<label for="billingAddressCountry">Country *</label>
					<input id="billingAddressCountry" pInputText formControlName="billingAddressCountry" />
					<small *ngIf="isInvalid('billingAddressCountry')" class="p-error">Required</small>
				</div>
			</div>
		</p-panel>

		<br />

		<!-- TAX & PAYMENT -->
		<p-panel header="Tax & Payment Details">
			<div class="field col-12 md:col-6">
				<label for="taxNumber">Tax Number</label>
				<input id="taxNumber" pInputText formControlName="taxNumber" />
			</div>
		</p-panel>

		<br />

		<!-- PLAN SELECTION -->
		<p-panel header="Plan Selection">
			<div class="grid formgrid">
				<div class="field col-12 md:col-6">
					<label for="pricingTierCode">Select Plan *</label>
					<p-dropdown
						id="pricingTierCode"
						formControlName="pricingTierCode"
						[options]="filteredPricingTiers"
						optionLabel="pricingTierName"
						optionValue="pricingTierCode"
						placeholder="Select your plan"
					>

						<ng-template let-plan pTemplate="item">
							<div class="flex justify-content-between align-items-center w-full">
								<span>{{ plan.pricingTierName }}</span>
								<span class="text-gray-500 text-sm">${{ getDisplayPrice(plan) }}/mo</span>
							</div>
						</ng-template>

						<ng-template let-plan pTemplate="selectedItem">
							<div *ngIf="plan" class="flex justify-content-between align-items-center w-full">
								<span>{{ plan.pricingTierName }}</span>
								<span class="text-gray-500 text-sm">${{ getDisplayPrice(plan) }}/mo</span>
							</div>
							<span *ngIf="!plan" class="text-gray-400">Select your plan</span>
						</ng-template>
					</p-dropdown>
					<small *ngIf="isInvalid('pricingTierCode')" class="p-error">Required</small>
				</div>

				<div class="field col-12 md:col-6">
					<label>Billing Cycle *</label>
					<div class="flex align-items-center gap-3 mt-2">
						<p-radioButton name="billingCycle" inputId="monthly" [value]="1" formControlName="billingCycle"></p-radioButton>
						<label for="monthly" class="mr-3">Billed Monthly</label>

						<p-radioButton name="billingCycle" inputId="annually" [value]="12" formControlName="billingCycle"></p-radioButton>
						<label for="annually">Billed Annually ({{ discountRate * 100 }}% off)</label>
					</div>
				</div>
			</div>
		</p-panel>

		<!-- SUBMIT -->
		<div class="mt-4 flex justify-content-end">
			<button
				pButton
				type="submit"
				[label]="saving ? 'Saving Information…' : 'Setup Billing'"
				class="p-button-primary"
				[disabled]="billingForm.invalid || saving"
			></button>
		</div>

		<p class="text-gray-600 mt-4 max-instructions-w mx-auto">
			By submitting, you agree to our
			<a href="#" class="text-primary underline">Terms of Use</a> and
			<a href="#" class="text-primary underline">Privacy Policy</a>.
			Your details will be reviewed within 1 business day and your first invoice will be sent once approved.
			<br />
			For changes after submission, please contact our Support Team at
			<a href="mailto:support@tyalls.com" class="text-primary underline">support&#64;tyalls.com</a>.
		</p>
	</form>
</div>
